import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search, FilterList, Error, Pending } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SupplierSelectionDialog from '../../components/SupplierSelectionDialog';
import { GetOrdersVm } from 'api/reception/get-orders.vm';
import { getOrders } from 'api/reception/get-orders';
import { OrdersStatus, OrderVm } from 'api/reception/order.vm';

const PurchaseOrdersPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<OrderVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [supplierName, setSupplierName] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);

  // Estado local para inputs do formulário
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get('supplier') || '');
  const [statusFilter, setStatusFilter] = useState<OrdersStatus | null>((searchParams.get('status') as OrdersStatus | null) || null);

  const updateSearchParams = (newParams: { search?: string; supplier?: string; status?: OrdersStatus | null }) => {
    const params = new URLSearchParams(searchParams);

    if (newParams.search !== undefined) {
      newParams.search ? params.set('search', newParams.search) : params.delete('search');
    }
    if (newParams.supplier !== undefined) {
      newParams.supplier ? params.set('supplier', newParams.supplier) : params.delete('supplier');
    }
    if (newParams.status !== undefined) {
      newParams.status ? params.set('status', newParams.status) : params.delete('status');
    }

    setSearchParams(params);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const fetchOrders = (
    offset: string | null,
    reset: boolean = false,
    search: string = searchTerm,
    supplier: string = supplierFilter,
    status: OrdersStatus | null = statusFilter
  ) => {
    setLoading(true);

    getOrders(offset || '', search, supplier, status, {
      success: (response: GetOrdersVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setOrders(reset ? response.objects : [...orders, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter encomendas:', err);
        setLoading(false);
      },
    });
  };

  // Carregamento inicial
  useEffect(() => {
    fetchOrders(null, true, searchTerm, supplierFilter, statusFilter);
    // eslint-disable-next-line
  }, []);

  const handleSearchSubmit = () => {
    updateSearchParams({
      search: searchTerm,
      supplier: supplierFilter,
      status: statusFilter,
    });
    fetchOrders(null, true);
  };

  const handleStatusFilter = (status: OrdersStatus | null) => {
    setStatusFilter(status);
    handleFilterClose();
  };

  const handleSupplierSelect = (supplierId: string, supplierName: string) => {
    setSupplierFilter(supplierId);
    setSupplierName(supplierName);
    setSupplierDialogOpen(false);
  };

  const handleClearSupplierFilter = () => {
    setSupplierFilter('');
    setSupplierName('');
  };

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchOrders(nextOffset);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleRowClick = (orderId: string) => {
    navigate(`/reception/purchase-orders/${encodeURIComponent(orderId)}?${searchParams.toString()}`);
  };

  const getStatusChip = (order: OrderVm) => {
    let label = '';
    let icon = <Pending fontSize="small" />;
    let color: 'error' | 'info';

    if (order.status === OrdersStatus.CLOSED) {
      label = 'Fechada';
      icon = <Error fontSize="small" />;
      color = 'error';
    } else {
      label = 'Pendente';
      icon = <Pending fontSize="small" />;
      color = 'info';
    }

    return <Chip icon={icon} label={label} color={color} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Encomendas de Fornecedor
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <TextField
              size="small"
              placeholder="Procurar encomendas..."
              InputProps={{
                startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
              }}
              sx={{ width: 300 }}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {supplierName ? (
                <Chip label={supplierName} onDelete={handleClearSupplierFilter} sx={{ mr: 1 }} />
              ) : (
                <Button variant="outlined" onClick={() => setSupplierDialogOpen(true)} sx={{ mr: 1 }}>
                  Filtrar por Fornecedor
                </Button>
              )}
            </Box>

            <IconButton aria-label="filter" onClick={handleFilterClick} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <FilterList />
            </IconButton>
          </Box>

          <Button variant="contained" onClick={handleSearchSubmit} disabled={loading} sx={{ ml: 'auto' }}>
            Procurar
          </Button>
        </Box>

        <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterClose}>
          <MenuItem onClick={() => handleStatusFilter(null)}>Todos os Estados</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(OrdersStatus.PENDING)}>Pendente</MenuItem>
          <MenuItem onClick={() => handleStatusFilter(OrdersStatus.CLOSED)}>Fechada</MenuItem>
        </Menu>
      </Paper>

      <SupplierSelectionDialog open={supplierDialogOpen} onClose={() => setSupplierDialogOpen(false)} onSelect={handleSupplierSelect} />

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID da Encomenda</TableCell>
              <TableCell>Série</TableCell>
              <TableCell>Fornecedor</TableCell>
              <TableCell>Criada Em</TableCell>
              <TableCell>Observações</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id} hover onClick={() => handleRowClick(order.id)} sx={{ cursor: 'pointer' }}>
                <TableCell>{order.id}</TableCell>
                <TableCell>{order.serieId}</TableCell>
                <TableCell>{order.supplierName}</TableCell>
                <TableCell>{order.orderDate ? new Date(order.orderDate).toLocaleDateString('pt-PT') : ''}</TableCell>
                <TableCell>{order.observations || '-'}</TableCell>
                <TableCell>{getStatusChip(order)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : orders.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais encomendas para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhuma encomenda encontrada
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PurchaseOrdersPage;
